import { useMemo } from 'react';
import {
  getActivityMixFromBuilding,
  getActivityMixFromDesign,
  getActivityMixFromStorey,
  getOtherActivityAreaFromActivityMix,
  getProgramActivityAreaFromActivityMix,
} from '@shared/helpers/design/activity-mix.helpers';
import { getGfaFromStoreys } from '@shared/helpers/design/shape.helpers';
import type { Storey } from '@shared/models/design/storey.model';
import { useBuildingDesign } from './design.hooks';
import {
  useHasVisibleBuildingSelection,
  useSelectedBuildingId,
  useSelectedStoreyId,
} from '../store/design-store.hooks';

export type DesignPanelScopeKind = 'project' | 'building' | 'storey';

/**
 * Data scope for the Design right panel. It reflects only the active selection;
 * hovering another building must not replace the panel's data.
 */
export const useDesignPanelScope = () => {
  const design = useBuildingDesign();
  const selectedBuildingId = useSelectedBuildingId();
  const selectedStoreyId = useSelectedStoreyId();
  const hasVisibleBuildingSelection = useHasVisibleBuildingSelection();

  return useMemo(() => {
    // Stale ids must not fall back to building 0 — use project scope instead.
    const selectedBuilding = hasVisibleBuildingSelection
      ? selectedBuildingId === undefined
        ? design.buildings[0]
        : design.buildings.find(
            (building) => building.id === selectedBuildingId,
          )
      : undefined;
    const selectedStorey = selectedBuilding?.storeys.find(
      (storey) => storey.id === selectedStoreyId,
    );

    const scope: { kind: DesignPanelScopeKind; storeys: Storey[] } =
      selectedStorey
        ? { kind: 'storey', storeys: [selectedStorey] }
        : selectedBuilding
          ? { kind: 'building', storeys: selectedBuilding.storeys }
          : {
              kind: 'project',
              storeys: design.buildings.flatMap((building) => building.storeys),
            };

    const activityMix = selectedStorey
      ? getActivityMixFromStorey(design, selectedStorey)
      : selectedBuilding
        ? getActivityMixFromBuilding(design, selectedBuilding)
        : getActivityMixFromDesign(design);

    return {
      ...scope,
      activityMix,
      totalGfa: getGfaFromStoreys(scope.storeys),
      nia: getProgramActivityAreaFromActivityMix(activityMix, true),
      otherActivityArea: getOtherActivityAreaFromActivityMix(activityMix),
    };
  }, [
    design,
    hasVisibleBuildingSelection,
    selectedBuildingId,
    selectedStoreyId,
  ]);
};
